import {Modal} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import type {IconName, ModalOutsidePress} from '@sollapay/ui/components';
import type {FC, ReactNode} from 'react';

type ConfirmActionModalProps = {
  isOpen: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void;
  onClose?: () => void;
  isLoading?: boolean;
  /** No visual by default — pass this for the rare confirm dialog that wants the circular icon badge. */
  icon?: IconName;
  iconVariant?: 'brand' | 'destructive';
  /** Custom visual rendered above the title with no circular background — takes precedence over `icon`. */
  illustration?: ReactNode;
  /** Force title/description alignment. Defaults to Modal's own rule: centered when there's no icon/illustration. */
  headerAlign?: 'start' | 'center';
  primaryVariant?: 'primary' | 'destructive';
  showCloseButton?: boolean;
  outsidePress?: ModalOutsidePress;
  isDirty?: boolean;
  discardDescription?: string;
  children?: ReactNode;
};

export const ConfirmActionModal: FC<ConfirmActionModalProps> = ({
  isOpen,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onClose,
  isLoading = false,
  icon,
  iconVariant,
  illustration,
  headerAlign,
  primaryVariant = 'destructive',
  showCloseButton = true,
  outsidePress,
  isDirty,
  discardDescription,
  children
}) => {
  const {t} = useTranslation();

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) onClose?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      icon={icon}
      iconVariant={iconVariant}
      illustration={illustration}
      headerAlign={headerAlign}
      title={title}
      description={description}
      primaryLabel={onConfirm ? (confirmLabel ?? t('confirm')) : undefined}
      primaryVariant={primaryVariant}
      primaryDisabled={isLoading}
      onPrimaryAction={onConfirm}
      secondaryLabel={onClose ? (cancelLabel ?? t('cancel')) : undefined}
      onSecondaryAction={onClose}
      showCloseButton={showCloseButton}
      outsidePress={outsidePress}
      isDirty={isDirty}
      discardDescription={discardDescription}
      size="sm"
    >
      {children}
    </Modal>
  );
};
